import type { BeforeSend } from "./utils";
import type { ErrorEvent, StackFrame } from "@sentry/react";

export const ignoreErrors = [
  /iframe adapter timedout/i,
  /request has been terminated/i,

  // Only reference I could find to these errors is in someone else's ignore list:
  // https://github.com/NDLANO/ndla-frontend/blob/master/src/util/sentry.ts?tab=readme-ov-file#L46
  /Request timeout .*Distributor\.getValue/,
  /Request timeout DefineExpirationForLanguagePacks\.getValue/,
  /Request timeout is(Dictate|Prediction|MathOcr)Available/,
  /Request timeout getDictionariesByLanguageId/,
] satisfies (string | RegExp)[];

// Every origin that can serve our own code: the apps and their static assets all live on a
// classdojo hostname (`teach.classdojo.com`, `static.classdojo.com`, `teach3.classdojo.com` for
// previews, `*.classdojo.test` locally). Mirrors toolkit's Sentry `allowUrls` pattern.
const OUR_HOSTNAME = /(^|\.)classdojo\.(com|test)$/i;

// Third-party scripts we load on purpose: Stripe checkout, the OneTrust consent banner, the Open
// Library search widget. Their frames are not ours to symbolicate, but they are not evidence of
// injected code either — a Stripe error caused by our own bad parameters has to survive this filter.
const DELIBERATE_VENDOR_HOSTNAME =
  /(^|\.)(stripe\.com|stripecdn\.com|stripe\.network|cookielaw\.org|openlibrary\.org|decagon\.ai)$/i;

/** Hostname of a stack frame's script, or undefined when the frame has no resolvable URL
 * (`<anonymous>`, `[native code]`, a bare eval frame, a local file path under a test runner).
 * Worker and module scripts created from an object URL keep our origin behind a `blob:` prefix,
 * so that prefix is peeled first. */
function frameHostname(path: string): string | undefined {
  try {
    return new URL(path.startsWith("blob:") ? path.slice("blob:".length) : path).hostname || undefined;
    // eslint-disable-next-line no-catch-all/no-catch-all -- an unparseable frame path tells us nothing about who owns the code
  } catch {
    return undefined;
  }
}

// `in_app` is deliberately not consulted: the browser stack parser marks EVERY frame `in_app: true`,
// extension frames included, so it carries no signal about whose code this is.
// Anything we cannot attribute is "unknown", never "foreign" — a stack is only discarded on positive
// evidence that part of it came from somewhere we did not put it.
function classifyFrame(frame: StackFrame): "ours" | "foreign" | "unknown" {
  const path = frame.abs_path ?? frame.filename;
  if (!path) return "unknown";

  const hostname = frameHostname(path);
  if (!hostname) return "unknown";

  if (typeof window !== "undefined" && hostname === window.location.hostname) return "ours";
  if (DELIBERATE_VENDOR_HOSTNAME.test(hostname)) return "unknown";

  return OUR_HOSTNAME.test(hostname) ? "ours" : "foreign";
}

// LinkedErrors splits a cause chain across `exception.values`, and values[0] is a CAUSE rather than
// the error that was thrown, so the whole chain has to be classified together.
function allFrames(event: ErrorEvent): StackFrame[] {
  return (event.exception?.values ?? []).flatMap((value) => value.stacktrace?.frames ?? []);
}

// eslint-disable-next-line complexity -- initial eslint9 migration
function isInjectedCode(event: ErrorEvent | undefined): boolean {
  if (!event) return false;

  const frames = event.exception?.values?.[0]?.stacktrace?.frames;

  if (!frames || frames.length === 0) return false;

  const firstFrame = frames[0];
  if (firstFrame?.filename === "<anonymous>") {
    return true;
  }

  // Deledao, The Smartest Web Filter for K12
  // this is causing an "onunhandledrejection timeout" error
  if (
    // frames[0] seems to be Sentry wrap function, but check it anyway
    frames[0]?.abs_path?.includes("script.deledao.com/") ||
    frames[1]?.abs_path?.includes("script.deledao.com/")
  ) {
    return true;
  }

  // General fallback for the vendors the checks above don't name (goguardian, imtlazarus,
  // singleview, safesearchinc, and whichever one ships next): a stack that reaches a foreign origin
  // and never touches one of ours is not our code, so nothing in it is actionable for us and it
  // cannot be symbolicated against our release either.
  // The asymmetry with the checks above is deliberate: those match a vendor by the first frames of
  // values[0] and stay as they were, while this one has to see the whole cause chain before it can
  // conclude that none of the stack is ours.
  const classifications = allFrames(event).map(classifyFrame);
  return classifications.includes("foreign") && !classifications.includes("ours");
}

export const ignoreWithBeforeSend: BeforeSend = (event) => {
  if (isInjectedCode(event)) {
    return null;
  }

  return event;
};
